import { SERVICES, PHONE_DISPLAY, PHONE_TEL, TID } from "@/config/site";
import {
  Clock,
  Truck,
  Wrench,
  AlertTriangle,
  Anchor,
  Zap,
  Key,
  CircleDot,
  Bike,
  Package,
  Map,
  Phone,
  ArrowRight,
} from "lucide-react";
import { Link } from "react-router-dom";

const ICONS = {
  clock: Clock,
  truck: Truck,
  wrench: Wrench,
  triangle: AlertTriangle,
  anchor: Anchor,
  zap: Zap,
  key: Key,
  circle: CircleDot,
  bike: Bike,
  package: Package,
  map: Map,
};

export default function Services() {
  return (
    <section id="services" className="relative bg-black py-24 sm:py-32 overflow-hidden">
      {/* Ambient spotlight */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[500px] opacity-40"
        style={{ background: "radial-gradient(ellipse at center, rgba(250,204,21,0.18) 0%, rgba(0,0,0,0) 65%)" }}
      />
      <div className="pointer-events-none absolute inset-0 bm-grain" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-16">
          <div className="max-w-3xl">
            <div className="font-display uppercase tracking-[0.32em] text-yellow-400 text-xs flex items-center gap-2">
              <span className="inline-block h-1.5 w-6 bg-yellow-400 rounded-full" />
              Our Services
            </div>
            <h2 className="mt-3 font-display font-black uppercase text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
              Every kind of tow. <span className="text-yellow-400">One phone number.</span>
            </h2>
            <p className="mt-5 text-zinc-400 text-base sm:text-lg font-light max-w-2xl leading-relaxed">
              From dead batteries at midnight to accident recovery on I-405, we handle it. Every job is quoted honestly on the phone — no surprise fees, no runaround.
            </p>
          </div>
          <a
            href={`tel:${PHONE_TEL}`}
            data-testid="services-call-btn"
            className="hidden lg:inline-flex items-center gap-2 bg-yellow-400 hover:bg-yellow-300 text-black font-display font-black uppercase tracking-wider px-6 py-3.5 rounded-full lux-glow-yellow transition-colors"
          >
            <Phone className="h-5 w-5" strokeWidth={3} /> {PHONE_DISPLAY}
          </a>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {SERVICES.map((s, i) => {
            const Icon = ICONS[s.icon] || Truck;
            const featured = i === 0;
            return (
              <Link
                key={s.slug}
                to={`/${s.slug}`}
                data-testid={TID.serviceCard(s.slug)}
                className={`lux-card lux-glass rounded-2xl p-6 flex flex-col gap-4 group relative overflow-hidden ${
                  featured ? "sm:col-span-2 lg:col-span-1 lg:row-span-2" : ""
                }`}
                style={{ animationDelay: `${i * 40}ms` }}
              >
                {/* Subtle yellow glow on hover */}
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute -top-24 -right-24 w-64 h-64 rounded-full opacity-0 group-hover:opacity-60 transition-opacity duration-500"
                  style={{ background: "radial-gradient(circle, rgba(250,204,21,0.35) 0%, rgba(0,0,0,0) 70%)" }}
                />
                <div className="relative flex items-center gap-4">
                  <div className="h-14 w-14 shrink-0 rounded-xl bg-yellow-400/10 border border-yellow-400/30 flex items-center justify-center group-hover:bg-yellow-400/20 transition-colors">
                    <Icon className="h-7 w-7 text-yellow-400" strokeWidth={2.2} />
                  </div>
                  <h3 className="font-display font-black uppercase text-white text-xl leading-tight tracking-tight">
                    {s.title}
                  </h3>
                </div>
                <p className="relative text-zinc-400 text-sm leading-relaxed font-light">
                  {s.short}
                </p>
                <div className="relative mt-auto flex items-center justify-between border-t border-white/5 pt-4">
                  <span className="font-display uppercase tracking-[0.25em] text-[10px] text-zinc-500">
                    24/7 Dispatch
                  </span>
                  <span className="inline-flex items-center gap-1 font-display uppercase tracking-widest text-xs text-yellow-400 group-hover:text-yellow-300 transition-colors">
                    Learn more <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-1" strokeWidth={3} />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
